import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { SidebarTab, SidebarItem, CanvasContent } from '../types/cockpit';
import { EnhancedDashboardTask, EnhancedDashboardEmail } from '../types/api';
import { useUser } from '../contexts/UserContext';
import { useSidebarData } from '../hooks/useSidebarData';
import { useTaskOperations } from '../hooks/useTaskOperations';
import UserAvatar from './UserAvatar';
import FileSystemExplorer from './FileSystemExplorer';
import SharePointFileExplorer from './SharePointFileExplorer';
import { LocalFile } from '../services/fileSystemService';
import LocalFileSystemService from '../services/fileSystemService';
import { SharePointItem } from '../types/api';
import { useOpenInCanvas } from '../contexts/GlobalCanvasContext';

/**
 * Props for the LeftSidebar component that manages navigation and content display
 * @interface LeftSidebarProps
 */
interface LeftSidebarProps {
  /** Currently active sidebar tab (clients, deals, files, workflows, tasks, emails) */
  activeTab: SidebarTab;
  /** Callback fired when user clicks on a different tab */
  onTabChange: (tab: SidebarTab) => void;
  /** Whether the sidebar is collapsed (hidden) or expanded */
  collapsed: boolean;
  /** Callback to toggle sidebar collapse/expand state */
  onToggleCollapse: () => void;
  /** Callback fired when user selects content to display in the main canvas area */
  onContentSelect: (content: CanvasContent | null) => void;
  /** Optional callback to refresh item counts (tasks/emails) */
  onCountChange?: () => void;
  /** Number of active (incomplete) tasks to display as badge */
  taskCount?: number;
  /** Number of unread emails to display as badge */
  emailCount?: number;
}

/**
 * Configuration object defining sidebar tabs with their display properties
 * @constant TAB_CONFIG
 * @type {Record<SidebarTab, {icon: string, label: string}>}
 */
const TAB_CONFIG: Record<SidebarTab, { icon: string; label: string }> = {
  clients: { icon: 'group', label: 'Clients' },
  deals: { icon: 'monetization_on', label: 'Deals' },
  files: { icon: 'description', label: 'Files' },
  tasks: { icon: 'task_alt', label: 'Tasks' },
  emails: { icon: 'mail', label: 'Emails' }
};

/**
 * LeftSidebar component providing navigation tabs and content selection
 * Manages tab navigation, item selection, file browsing, and user profile display
 *
 * @component
 * @param {LeftSidebarProps} props - Component props
 * @returns {JSX.Element} Rendered sidebar component
 *
 * @example
 * ```tsx
 * <LeftSidebar
 *   activeTab="tasks"
 *   onTabChange={(tab) => setActiveTab(tab)}
 *   collapsed={false}
 *   onToggleCollapse={() => setCollapsed(!collapsed)}
 *   onContentSelect={(content) => setCanvasContent(content)}
 *   taskCount={5}
 *   emailCount={3}
 * />
 * ```
 */
const LeftSidebar: React.FC<LeftSidebarProps> = ({
  activeTab,
  onTabChange,
  collapsed,
  onToggleCollapse,
  onContentSelect,
  onCountChange,
  taskCount = 0,
  emailCount = 0
}) => {
  const { userProfile, signOut } = useUser();
  const { openTab, openFile, openTask, openEmail, openOpportunity, openPerson } = useOpenInCanvas();
  const { sidebarData, fullTasksData, fullEmailsData, loading, loadSidebarData } = useSidebarData();
  const { completeTask } = useTaskOperations();
  const [todoListId] = useState<string>('defaultList');
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const [customFolderPath, setCustomFolderPath] = useState('');
  const [selectedCustomFolder, setSelectedCustomFolder] = useState<{id: string, name: string, path: string, dirHandle?: FileSystemDirectoryHandle, files?: File[]} | null>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [fileSource, setFileSource] = useState<'sharepoint' | 'local'>('sharepoint');
  const menuRef = useRef<HTMLDivElement>(null);

  // Load sidebar data when active tab changes
  useEffect(() => {
    loadSidebarData(activeTab);
  }, [activeTab, loadSidebarData]);

  // Handle clicks outside the menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  // Memoize computed values
  const currentData = useMemo(() => sidebarData[activeTab] || [], [sidebarData, activeTab]);

  /**
   * Get the count for a specific tab for badge display
   *
   * @param {string} tabKey - The tab identifier ('tasks' or 'emails')
   * @returns {number} The count to display as a badge, or 0 if not applicable
   *
   * @example
   * ```typescript
   * const taskBadge = getCount('tasks'); // Returns taskCount prop value
   * const emailBadge = getCount('emails'); // Returns emailCount prop value
   * const clientBadge = getCount('clients'); // Returns 0
   * ```
   */
  const getCount = useCallback((tabKey: string) => {
    if (tabKey === 'tasks') return taskCount;
    if (tabKey === 'emails') return emailCount;
    return 0;
  }, [taskCount, emailCount]);

  /**
   * Format a date as a relative time string for display in the sidebar
   *
   * @param {Date} date - The date to format relative to now
   * @returns {string} Human-readable relative time string (e.g., "2h ago", "1d ago", "Now")
   *
   * @example
   * ```typescript
   * const recentDate = new Date(Date.now() - 2 * 60 * 60 * 1000); // 2 hours ago
   * formatTimeAgo(recentDate); // Returns "2h ago"
   *
   * const oldDate = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000); // 3 days ago
   * formatTimeAgo(oldDate); // Returns "3d ago"
   *
   * const veryRecent = new Date(Date.now() - 30 * 60 * 1000); // 30 minutes ago
   * formatTimeAgo(veryRecent); // Returns "Now"
   * ```
   */
  const formatTimeAgo = useCallback((date: Date) => {
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return '1d ago';
    return `${Math.floor(diffInHours / 24)}d ago`;
  }, []);

  /**
   * Get suggested folder paths for the fallback folder picker dialog
   * Provides common Windows user directories as quick-select options
   *
   * @returns {Array<{name: string, path: string}>} Array of folder suggestions with display names and paths
   *
   * @example
   * ```typescript
   * const suggestions = getFolderSuggestions();
   * // Returns: [
   * //   { name: 'Documents', path: 'C:\\Users\\UserName\\Documents' },
   * //   { name: 'Downloads', path: 'C:\\Users\\UserName\\Downloads' },
   * //   ...
   * // ]
   * ```
   */
  const getFolderSuggestions = useCallback(() => {
    const userProfile = process.env.USERPROFILE || 'C:\\Users\\' + (process.env.USERNAME || 'User');
    return [
      { name: 'Documents', path: userProfile + '\\Documents' },
      { name: 'Downloads', path: userProfile + '\\Downloads' },
      { name: 'OneDrive', path: userProfile + '\\OneDrive' },
      { name: 'OneDrive - Business', path: userProfile + '\\OneDrive - Prosper Finance' },
      { name: 'Desktop', path: userProfile + '\\Desktop' }
    ];
  }, []);

  /**
   * Handles user clicking on a sidebar item with real-time data synchronization
   * Opens content in global canvas based on item type, ensuring full data is loaded from APIs
   *
   * @param {SidebarItem} item - The sidebar item that was clicked containing basic metadata
   * @returns {Promise<void>} Resolves when content is successfully opened in canvas
   *
   * @description
   * This function implements real-time synchronization by:
   * - For tasks: Fetches complete task data from Microsoft Graph/Planner API
   * - For emails: Loads enhanced email details with attachments and full body content
   * - For deals: Opens opportunity data from CRM system
   * - Updates selected item state for visual feedback
   *
   * @example
   * ```typescript
   * // Task item click - loads full task data from API
   * const taskItem = {
   *   id: 'task-123',
   *   type: 'task',
   *   title: 'Review quarterly proposal',
   *   subtitle: 'Due tomorrow',
   *   lastModified: new Date()
   * };
   * handleItemClick(taskItem); // Opens task in canvas with real-time data
   *
   * // Email item click - loads enhanced email from Microsoft Graph
   * const emailItem = {
   *   id: 'email-456',
   *   type: 'email',
   *   title: 'Project Update',
   *   subtitle: 'from john.doe@company.com'
   * };
   * handleItemClick(emailItem); // Opens email with full body, attachments, etc.
   *
   * // Deal item click - opens opportunity details
   * const dealItem = {
   *   id: 'deal-789',
   *   type: 'deal',
   *   title: 'ABC Corp Acquisition',
   *   dealData: { value: 500000, stage: 'negotiation' }
   * };
   * handleItemClick(dealItem); // Opens deal with full opportunity data
   * ```
   */
  const handleItemClick = useCallback(async (item: SidebarItem) => {
    setSelectedItemId(item.id);

    // For tasks, we need to pass the full task data
    let contentData: SidebarItem | EnhancedDashboardTask | EnhancedDashboardEmail = item;

    if (item.type === 'task') {
      // Find the full task data by matching the actual task ID
      const fullTask = fullTasksData.find(task => task.id === item.id);
      if (fullTask) {
        contentData = fullTask;
      }
    } else if (item.type === 'email') {
      try {
        // For emails, we need to load enhanced email details
        // First, find the basic email data by index
        const emailIndex = parseInt(item.id.replace('email-', '')) || 0;
        const basicEmail = fullEmailsData[emailIndex];

        if (basicEmail) {
          // Use REAL email data from Microsoft Graph API
          const enhancedEmail: EnhancedDashboardEmail = {
            id: basicEmail.id, // Use REAL email ID from Microsoft Graph
            subject: basicEmail.subject,
            sender: basicEmail.sender,
            senderEmail: basicEmail.senderEmail || basicEmail.sender,
            date: basicEmail.date,
            body: basicEmail.bodyPreview || `Email content for: ${basicEmail.subject}`,
            bodyPreview: basicEmail.bodyPreview || basicEmail.subject,
            isRead: basicEmail.isRead || false,
            hasAttachments: basicEmail.hasAttachments || false,
            attachments: basicEmail.attachments || [],
            conversationId: basicEmail.conversationId,
            importance: basicEmail.importance || 'normal',
            categories: basicEmail.categories || []
          };

          contentData = enhancedEmail;
        }
      } catch (error) {
        // Error handling without console.log for production
      }
    }

    // Open in global canvas instead of content selection
    console.log('🔍 Item clicked:', {
      type: item.type,
      hasContentData: !!contentData,
      hasDealData: !!(item as any).dealData,
      title: item.title
    });

    if (item.type === 'task' && contentData) {
      console.log('📋 Opening task in tab');
      openTask(contentData as EnhancedDashboardTask);
    } else if (item.type === 'email' && contentData) {
      console.log('📧 Opening email in tab');
      openEmail(contentData as EnhancedDashboardEmail);
    } else if (item.type === 'deal' && (item as any).dealData) {
      console.log('💰 Opening deal in tab');
      // Open deal using the opportunity function with full deal data
      openOpportunity((item as any).dealData);
    } else if (item.type === 'client') {
      console.log('👤 Opening client in tab');
      // Use personData stored on sidebar item (includes deals, employment, etc.)
      const personData = (item as any).personData;
      const clientData = {
        ...(personData || contentData),
        name: personData?.name || item.title,
        displayName: personData?.name || item.title,
        status: personData?.status || item.subtitle || 'Active',
        id: personData?.id || item.id,
        deals: personData?.deals || [],
        dealFolders: personData?.dealFolders || [],
        employment: personData?.employment || []
      };
      openPerson(clientData);
    } else if (item.type === 'file') {
      console.log('📁 Opening file in tab');
      // Open file
      openFile(contentData || item);
    } else {
      console.log('⚠️ Falling back to legacy content selection for type:', item.type);
      // Fallback to legacy content selection for other types
      const content: CanvasContent = {
        type: item.type as any,
        id: item.id,
        title: item.title,
        data: contentData,
        listId: todoListId
      };
      onContentSelect(content);
    }
  }, [fullTasksData, fullEmailsData, openTask, openEmail, openOpportunity, openPerson, openFile, todoListId, onContentSelect]);

  /**
   * Handles file selection from the local file system with secure path handling
   * Creates canvas content for the selected file and updates navigation state
   *
   * @param {LocalFile} file - The file object selected from the file system explorer
   * @returns {void}
   *
   * @description
   * This function manages local file system integration by:
   * - Setting the selected item state for visual feedback
   * - Creating canvas content with file metadata and directory handle
   * - Preserving folder context for relative file operations
   * - Enabling drag-and-drop functionality for selected files
   *
   * @example
   * ```typescript
   * // File selection from Documents folder
   * const selectedFile = {
   *   id: 'file-123',
   *   name: 'quarterly-report.pdf',
   *   path: 'C:\\Users\\User\\Documents\\quarterly-report.pdf',
   *   size: 2457600,
   *   lastModified: new Date('2024-01-15'),
   *   type: 'application/pdf'
   * };
   * handleFileSystemFileSelect(selectedFile);
   * // Creates canvas content and opens file for viewing/editing
   *
   * // File with directory handle for advanced operations
   * const fileWithHandle = {
   *   ...selectedFile,
   *   dirHandle: directoryHandle // For folder-relative operations
   * };
   * handleFileSystemFileSelect(fileWithHandle);
   * ```
   */
  const handleFileSystemFileSelect = useCallback((file: LocalFile) => {
    setSelectedItemId(`file-${file.id}`);

    const content: CanvasContent = {
      type: 'file' as any,
      id: file.id,
      title: file.name,
      data: {
        ...file,
        dirHandle: selectedCustomFolder?.dirHandle,
        rootPath: selectedCustomFolder?.path
      }
    };

    onContentSelect(content);
  }, [selectedCustomFolder, onContentSelect]);

  /**
   * Handles double-click on file system files to open them directly in canvas
   * Bypasses content selection and opens file immediately using global canvas context
   *
   * @param {LocalFile} file - The file object that was double-clicked
   * @returns {void}
   *
   * @description
   * This function provides direct file opening functionality:
   * - Uses global canvas context's openFile method for immediate opening
   * - Skips the intermediate content selection step
   * - Enables quick access to frequently used files
   * - Maintains file system context and directory handles
   *
   * @example
   * ```typescript
   * // User double-clicks on a document file
   * const documentFile = {
   *   id: 'file-123',
   *   name: 'meeting-notes.docx',
   *   path: 'C:\\Users\\User\\Documents\\meeting-notes.docx',
   *   size: 51200,
   *   lastModified: new Date()
   * };
   * handleFileSystemFileDoubleClick(documentFile);
   * // File opens immediately in canvas without selection dialog
   * ```
   */
  const handleFileSystemFileDoubleClick = useCallback((file: LocalFile) => {
    openFile(file);
  }, [openFile]);

  /**
   * Handles drag start event for file system files to enable drag-and-drop functionality
   * Prepares file data for transfer to AI chat component or other drop targets
   *
   * @param {LocalFile} file - The file object being dragged from the file system
   * @returns {void}
   *
   * @description
   * This function enables drag-and-drop integration with the AI chat component:
   * - Sets up drag data transfer for file objects
   * - Enables users to drag files from sidebar to chat for processing
   * - Prepares file metadata for secure transfer
   * - Maintains file system context during drag operations
   *
   * @example
   * ```typescript
   * // User starts dragging a file from the file explorer
   * const draggedFile = {
   *   id: 'file-123',
   *   name: 'contract.pdf',
   *   path: 'C:\\Users\\User\\Documents\\contract.pdf',
   *   size: 1024000,
   *   lastModified: new Date()
   * };
   * handleFileSystemFileDragStart(draggedFile);
   * // File can now be dropped into AI chat for analysis or processing
   * ```
   */
  const handleSharePointFileSelect = useCallback((file: SharePointItem, path: string) => {
    if (!file.isFolder) {
      setSelectedItemId(`sp-${file.id}`);
    }
  }, []);

  const handleSharePointFileDoubleClick = useCallback((file: SharePointItem, path: string) => {
    if (!file.isFolder) {
      const filePath = path ? `${path}/${file.name}` : file.name;
      openFile({
        id: file.id,
        name: file.name,
        type: 'file',
        size: file.size,
        lastModified: file.lastModified ? new Date(file.lastModified) : new Date(),
        fullPath: filePath,
        relativePath: filePath,
        extension: file.extension || undefined,
        webUrl: file.webUrl,
        source: 'sharepoint'
      });
    }
  }, [openFile]);

  const handleFileSystemFileDragStart = useCallback((file: LocalFile) => {
    // This will be handled by the AI chat component to accept the dropped file
  }, []);

  /**
   * Opens native file system dialog for folder selection with progressive enhancement
   * Uses modern File System API when available, with secure fallback mechanisms
   *
   * @returns {Promise<void>} Resolves when folder selection process completes
   *
   * @description
   * This function implements progressive enhancement for file system access:
   * 1. Attempts modern File System API for secure directory handles
   * 2. Falls back to manual path input if API unavailable
   * 3. Sets selected folder state for file system exploration
   * 4. Handles permission errors and user cancellation gracefully
   * 5. Updates UI state and triggers folder picker dialog when needed
   *
   * @example
   * ```typescript
   * // User clicks "Browse" button
   * await handleNativeFolderSelect();
   *
   * // Modern browsers: Opens native folder picker with directory handle
   * // Result: { success: true, folderPath: "C:\\Users\\User\\Documents", dirHandle: handle }
   *
   * // Legacy browsers: Falls back to manual path input dialog
   * // Result: Shows custom folder picker with suggestions
   *
   * // Error handling: Permission denied or API unavailable
   * // Result: Gracefully falls back to manual input method
   * ```
   *
   * @security
   * - No direct file system access without user permission
   * - Directory handles are scoped to selected folder only
   * - Fallback method requires explicit user path input
   * - No automatic scanning or indexing of unselected directories
   */
  const handleNativeFolderSelect = useCallback(async () => {
    try {
      const result = await LocalFileSystemService.selectFolderWithNativeDialog();

      if (result.cancelled) {
        return;
      }

      if (result.success && result.folderPath) {
        const folder = {
          id: `native-${Date.now()}`,
          name: result.folderName || result.folderPath.split(/[\\/]/).pop() || 'Selected Folder',
          path: result.folderPath,
          dirHandle: result.dirHandle,
          files: result.files
        };

        setSelectedCustomFolder(folder);
      } else if (result.error) {
        setShowFolderPicker(true);
      }
    } catch (error) {
      setShowFolderPicker(true);
    }
  }, []);

  /**
   * Handles manual folder path input and validation for fallback folder selection
   * Creates folder object from user-provided path and updates file system state
   *
   * @returns {void}
   *
   * @description
   * This function processes manual folder path input:
   * - Validates that a path has been entered
   * - Extracts folder name from path for display purposes
   * - Creates folder object with unique ID and path
   * - Updates selected folder state for file system exploration
   * - Closes the fallback folder picker dialog
   *
   * @example
   * ```typescript
   * // User enters path in fallback dialog
   * customFolderPath = "C:\\Users\\User\\Projects\\LexAI"
   *
   * handleCustomFolderSelect();
   * // Result: Folder object created and dialog closed
   * // selectedCustomFolder = {
   * //   id: 'custom-1703123456789',
   * //   name: 'LexAI',
   * //   path: 'C:\\Users\\User\\Projects\\LexAI'
   * // }
   * ```
   */
  const handleCustomFolderSelect = useCallback(() => {
    if (!customFolderPath.trim()) return;

    const folder = {
      id: `custom-${Date.now()}`,
      name: customFolderPath.split(/[/\\]/).pop() || customFolderPath,
      path: customFolderPath.trim()
    };

    setSelectedCustomFolder(folder);
    setShowFolderPicker(false);
  }, [customFolderPath]);

  /**
   * Handles closing of the fallback folder picker dialog
   * Resets dialog state and clears any entered folder path
   *
   * @returns {void}
   *
   * @description
   * This function manages dialog cleanup when user cancels folder selection:
   * - Hides the folder picker dialog
   * - Clears any entered custom folder path
   * - Resets form state for future use
   * - Provides clean exit from fallback folder selection flow
   *
   * @example
   * ```typescript
   * // User clicks "Cancel" or "✕" in folder picker dialog
   * handleFolderPickerClose();
   * // Result: Dialog hidden, customFolderPath cleared, showFolderPicker = false
   * ```
   */
  const handleFolderPickerClose = useCallback(() => {
    setShowFolderPicker(false);
    setCustomFolderPath('');
  }, []);


  /**
   * Handles task completion toggle with real-time API synchronization
   * Updates task status via Microsoft Graph API and refreshes sidebar data
   *
   * @param {string} taskId - Unique identifier of the task to toggle completion status
   * @param {React.MouseEvent} event - Click event to prevent event bubbling to parent elements
   * @returns {Promise<void>} Resolves when task update completes and UI refreshes
   *
   * @description
   * This function manages task completion state with full API integration:
   * - Prevents event bubbling to avoid triggering item selection
   * - Calls completeTask API with current task data
   * - Refreshes sidebar data to reflect updated task counts
   * - Triggers onCountChange callback for parent component updates
   * - Handles errors gracefully without breaking the UI flow
   *
   * @example
   * ```typescript
   * // User clicks checkbox on incomplete task
   * const taskId = 'task-123';
   * const clickEvent = new MouseEvent('click');
   *
   * await handleTaskToggle(taskId, clickEvent);
   * // Result: Task marked complete via API, sidebar refreshes, count updates
   *
   * // User clicks checkbox on completed task
   * await handleTaskToggle('task-456', clickEvent);
   * // Result: Task marked incomplete, UI state updated accordingly
   * ```
   *
   * @api-integration
   * - Microsoft Graph API (Tasks/Planner) for task completion updates
   * - Real-time synchronization with backend task service
   * - Automatic UI refresh after successful API calls
   */
  const handleTaskToggle = useCallback(async (taskId: string, event: React.MouseEvent) => {
    event.stopPropagation();

    const success = await completeTask(taskId, fullTasksData);
    if (success) {
      loadSidebarData(activeTab);

      if (onCountChange) {
        onCountChange();
      }
    }
  }, [completeTask, fullTasksData, loadSidebarData, activeTab, onCountChange]);

  /**
   * Handles user sign out
   */
  const handleSignOut = useCallback(async () => {
    console.log('🚪 Sign out clicked');
    try {
      setShowUserMenu(false);
      console.log('🚪 Calling signOut function');
      await signOut();
      console.log('✅ Sign out completed');
    } catch (error) {
      console.error('❌ Error during sign out:', error);
    }
  }, [signOut]);

  /**
   * Toggles the user menu dropdown
   */
  const toggleUserMenu = useCallback(() => {
    console.log('🔧 User menu toggle clicked');
    setShowUserMenu(prev => {
      console.log('🔧 User menu visibility:', !prev);
      return !prev;
    });
  }, []);

  /**
   * Opens an artifact view (all clients, all deals, etc.) in a new canvas tab
   */
  const openArtifactTab = useCallback((type: 'clients' | 'deals' | 'tasks' | 'emails') => {
    const tabConfig: Record<string, { title: string; icon: string; type: 'clients-list' | 'deals-list' | 'tasks-list' | 'emails-list' }> = {
      'clients': { title: 'All Clients', icon: 'people', type: 'clients-list' as const },
      'deals': { title: 'All Deals', icon: 'monetization_on', type: 'deals-list' as const },
      'tasks': { title: 'All Tasks', icon: 'task_alt', type: 'tasks-list' as const },
      'emails': { title: 'All Emails', icon: 'mail', type: 'emails-list' as const }
    };

    const config = tabConfig[type];
    openTab({
      title: config.title,
      type: config.type,
      icon: config.icon,
      content: {}
    });
  }, [openTab]);


  return (
    <aside
      className={`left-sidebar ${collapsed ? 'collapsed' : ''}`}
      role="complementary"
      aria-label="Navigation sidebar"
    >
      {/* Navigation Tabs - Professional Flush Style */}
      <nav role="tablist" aria-label="Main navigation" className="sidebar-tabs-flush">
        {Object.entries(TAB_CONFIG).map(([tabKey, config]) => {
          const count = getCount(tabKey);

          return (
            <button
              key={tabKey}
              className={`sidebar-tab-flush ${activeTab === tabKey ? 'sidebar-tab-flush-active' : ''}`}
              onClick={() => onTabChange(tabKey as SidebarTab)}
              onDoubleClick={() => {
                // Only open artifact tabs for these types
                if (tabKey === 'clients' || tabKey === 'deals' || tabKey === 'tasks' || tabKey === 'emails') {
                  openArtifactTab(tabKey);
                }
              }}
              title={config.label}
              role="tab"
              aria-selected={activeTab === tabKey ? "true" : "false"}
              aria-controls={`tabpanel-${tabKey}`}
              aria-label={`${config.label} tab${count > 0 ? ` (${count} items)` : ''}`}
            >
              <span className="material-symbols-outlined" aria-hidden="true">{config.icon}</span>
              <span className="sidebar-tab-label" style={{ display: 'none' }}>{config.label}</span>
              {count > 0 && (
                <span className="sidebar-tab-badge" aria-label={`${count} ${tabKey}`}>
                  {count > 99 ? '99+' : count}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Content Area */}
      {!collapsed && (
        <div
          className="sidebar-content"
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <h2 className="sidebar-section-header" style={{ margin: 0 }} id={`tab-${activeTab}`}>{TAB_CONFIG[activeTab].label}</h2>
            {activeTab === 'files' && (
              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  onClick={() => setFileSource('sharepoint')}
                  style={{
                    background: fileSource === 'sharepoint' ? 'var(--obsidian-accent)' : 'transparent',
                    color: fileSource === 'sharepoint' ? 'white' : 'var(--obsidian-text-secondary)',
                    border: fileSource === 'sharepoint' ? 'none' : '1px solid var(--obsidian-border)',
                    borderRadius: '4px',
                    padding: '3px 8px',
                    cursor: 'pointer',
                    fontSize: '11px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px'
                  }}
                  title="Browse SharePoint sandbox files"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>cloud</span>
                  SharePoint
                </button>
                <button
                  onClick={() => { setFileSource('local'); if (!selectedCustomFolder) handleNativeFolderSelect(); }}
                  style={{
                    background: fileSource === 'local' ? 'var(--obsidian-accent)' : 'transparent',
                    color: fileSource === 'local' ? 'white' : 'var(--obsidian-text-secondary)',
                    border: fileSource === 'local' ? 'none' : '1px solid var(--obsidian-border)',
                    borderRadius: '4px',
                    padding: '3px 8px',
                    cursor: 'pointer',
                    fontSize: '11px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px'
                  }}
                  title="Browse local files"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>folder_open</span>
                  Local
                </button>
              </div>
            )}
          </div>

          {activeTab === 'files' ? (
            fileSource === 'sharepoint' ? (
              <SharePointFileExplorer
                onFileSelect={handleSharePointFileSelect}
                onFileDoubleClick={handleSharePointFileDoubleClick}
                className="sidebar-file-explorer"
              />
            ) : selectedCustomFolder ? (
              <FileSystemExplorer
                onFileSelect={handleFileSystemFileSelect}
                onFileDoubleClick={handleFileSystemFileDoubleClick}
                onFileDragStart={handleFileSystemFileDragStart}
                className="sidebar-file-explorer"
                showSearch={true}
                allowDragDrop={true}
                customFolder={selectedCustomFolder}
              />
            ) : (
              <div style={{
                padding: '2rem',
                textAlign: 'center',
                color: 'var(--obsidian-text-secondary)'
              }}>
                <span className="material-symbols-outlined" style={{
                  fontSize: '4rem',
                  color: 'var(--obsidian-text-disabled)',
                  marginBottom: '1rem',
                  display: 'block'
                }}>
                  folder_open
                </span>
                <h3 style={{
                  color: 'var(--obsidian-text-primary)',
                  marginBottom: '1rem',
                  fontSize: '1.2rem'
                }}>
                  No Folder Selected
                </h3>
                <p style={{
                  marginBottom: '1.5rem',
                  lineHeight: '1.5',
                  fontSize: '0.95rem'
                }}>
                  Click "Local" above to select a folder from your computer, or switch to "SharePoint" to browse sandbox files.
                </p>
              </div>
            )
          ) : loading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '2rem',
                color: 'var(--obsidian-text-secondary)'
              }}
              role="status"
              aria-live="polite"
              aria-label="Loading content"
            >
              <div className="loading-dots" aria-hidden="true">
                <div className="loading-dot"></div>
                <div className="loading-dot"></div>
                <div className="loading-dot"></div>
              </div>
            </div>
          ) : (
            <nav style={{ marginTop: '8px' }} aria-label={`${TAB_CONFIG[activeTab].label} navigation`}>
              {currentData.map((item) => (
                <div
                  key={item.id}
                  className={`sidebar-item ${selectedItemId === item.id ? 'active' : ''}`}
                  onClick={() => handleItemClick(item)}
                  role="button"
                  tabIndex={0}
                  aria-label={`${item.type}: ${item.title}${item.subtitle ? ` - ${item.subtitle}` : ''}`}
                  aria-selected={selectedItemId === item.id ? "true" : "false"}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleItemClick(item);
                    }
                  }}
                >
                  <div className="item-content">
                    {item.type === 'task' ? (
                      <button
                        onClick={(e) => handleTaskToggle(item.id, e)}
                        style={{
                          width: '18px',
                          height: '18px',
                          borderRadius: '4px',
                          border: '2px solid var(--obsidian-border)',
                          backgroundColor: 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          flexShrink: 0,
                          marginRight: '8px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = 'var(--obsidian-accent)';
                          e.currentTarget.style.backgroundColor = 'var(--obsidian-accent-10)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'var(--obsidian-border)';
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        title={`Complete task: ${item.title}`}
                        aria-label={`Complete task: ${item.title}`}
                        aria-pressed="false"
                      />
                    ) : (
                      <span
                        className="item-icon material-symbols-outlined"
                        aria-hidden="true"
                      >
                        {item.type === 'folder' ? 'folder' :
                         item.type === 'file' ? 'description' :
                         item.type === 'client' ? 'person' :
                         item.type === 'deal' ? 'handshake' :
                         item.type === 'email' ? 'mail' : 'description'}
                      </span>
                    )}
                    <div className="item-details">
                      <div className="item-title">{item.title}</div>
                      {item.subtitle && <div className="item-subtitle">{item.subtitle}</div>}
                    </div>
                  </div>
                  {item.lastModified && (
                    <span className="item-time" aria-label={`Last modified ${formatTimeAgo(item.lastModified)}`}>
                      {formatTimeAgo(item.lastModified)}
                    </span>
                  )}
                </div>
              ))}

              {currentData.length === 0 && !loading && (
                <div style={{
                  padding: '2rem 1rem',
                  textAlign: 'center',
                  color: 'var(--obsidian-text-secondary)',
                  fontSize: '14px'
                }}>
                  No {TAB_CONFIG[activeTab].label.toLowerCase()} found
                </div>
              )}
            </nav>
          )}
        </div>
      )}

      {/* User Profile */}
      {!collapsed && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px',
            borderTop: '1px solid var(--obsidian-border)',
            flexShrink: 0
          }}
          role="region"
          aria-label="User profile"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <UserAvatar size="small" showDropdown={false} />
            <span
              style={{
                fontSize: '14px',
                fontWeight: 500,
                color: 'var(--obsidian-text-primary)'
              }}
              aria-label={`Current user: ${userProfile?.displayName || `${userProfile?.givenName || ''} ${userProfile?.surname || ''}`.trim() || 'User'}`}
            >
              {userProfile?.displayName || `${userProfile?.givenName || ''} ${userProfile?.surname || ''}`.trim() || 'User'}
            </span>
          </div>
          <div className="user-menu-container" ref={menuRef} style={{ position: 'relative' }}>
            <button
              className="sidebar-toggle-btn"
              aria-label="User menu"
              title="User menu"
              onClick={toggleUserMenu}
              aria-expanded={showUserMenu ? "true" : "false"}
              aria-haspopup="true"
            >
              <span className="material-symbols-outlined" aria-hidden="true">more_horiz</span>
            </button>

            {showUserMenu && (
              <>
                {console.log('🔧 Rendering user menu dropdown')}
                <div
                  className="user-menu-dropdown"
                  role="menu"
                  aria-label="User menu options"
                  style={{
                    position: 'absolute',
                    bottom: 'calc(100% + 8px)',
                    right: '0',
                    backgroundColor: 'var(--obsidian-bg, #ffffff)',
                    border: '1px solid var(--obsidian-border, #e1e5e9)',
                    borderRadius: '8px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                    minWidth: '160px',
                    zIndex: 999999,
                    padding: '4px 0',
                    transform: 'translateY(0)',
                    opacity: 1,
                    visibility: 'visible'
                  }}
                >
                <button
                  className="user-menu-item"
                  onClick={() => {
                    console.log('🚪 Sign out button clicked');
                    handleSignOut();
                  }}
                  role="menuitem"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: 'var(--obsidian-text-primary)',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    borderRadius: '8px 8px 0 0',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLElement).style.backgroundColor = 'var(--obsidian-hover-bg)';
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLElement).style.backgroundColor = 'transparent';
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '18px' }} aria-hidden="true">logout</span>
                  Sign Out
                </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Fallback Folder Picker Dialog */}
      {showFolderPicker && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="folder-picker-title"
        >
          <div
            style={{
              backgroundColor: 'var(--obsidian-bg)',
              border: '1px solid var(--obsidian-border)',
              borderRadius: '8px',
              padding: '20px',
              minWidth: '400px',
              maxWidth: '600px'
            }}
            role="document"
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}>
              <h3
                id="folder-picker-title"
                style={{ margin: 0, color: 'var(--obsidian-text-primary)' }}
              >
                Select Folder
              </h3>
              <button
                onClick={handleFolderPickerClose}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--obsidian-text-secondary)',
                  cursor: 'pointer',
                  fontSize: '18px'
                }}
                aria-label="Close folder picker"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div style={{
              marginBottom: '16px',
              padding: '8px',
              backgroundColor: 'var(--obsidian-hover-bg)',
              borderRadius: '4px',
              fontSize: '12px',
              color: 'var(--obsidian-text-secondary)'
            }}>
              💡 <strong>Fallback Mode:</strong> The native folder dialog wasn't available.
              Please paste your folder path below or use the quick select buttons.
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label
                htmlFor="folder-path-input"
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'var(--obsidian-text-primary)',
                  fontSize: '14px'
                }}
              >
                Folder Path:
              </label>
              <input
                id="folder-path-input"
                type="text"
                value={customFolderPath}
                onChange={(e) => setCustomFolderPath(e.target.value)}
                placeholder="e.g., C:\Users\YourName\Documents"
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid var(--obsidian-border)',
                  backgroundColor: 'var(--obsidian-bg)',
                  color: 'var(--obsidian-text-primary)',
                  fontSize: '14px'
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCustomFolderSelect();
                  }
                }}
                autoFocus
                aria-describedby="folder-path-help"
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label
                id="quick-select-label"
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'var(--obsidian-text-primary)',
                  fontSize: '14px'
                }}
              >
                Quick Select:
              </label>
              <div
                style={{ display: 'grid', gap: '4px' }}
                role="group"
                aria-labelledby="quick-select-label"
              >
                {getFolderSuggestions().map((suggestion) => (
                  <button
                    key={suggestion.path}
                    onClick={() => setCustomFolderPath(suggestion.path)}
                    style={{
                      padding: '6px 8px',
                      border: '1px solid var(--obsidian-border)',
                      borderRadius: '4px',
                      backgroundColor: 'var(--obsidian-hover-bg)',
                      color: 'var(--obsidian-text-primary)',
                      cursor: 'pointer',
                      fontSize: '12px',
                      textAlign: 'left',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLElement).style.backgroundColor = 'var(--obsidian-accent-bg)';
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLElement).style.backgroundColor = 'var(--obsidian-hover-bg)';
                    }}
                    aria-label={`Select ${suggestion.name} folder at ${suggestion.path}`}
                  >
                    📁 {suggestion.name} - {suggestion.path}
                  </button>
                ))}
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                gap: '8px',
                justifyContent: 'flex-end'
              }}
              role="group"
              aria-label="Folder selection actions"
            >
              <button
                onClick={handleFolderPickerClose}
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--obsidian-border)',
                  borderRadius: '4px',
                  backgroundColor: 'var(--obsidian-bg)',
                  color: 'var(--obsidian-text-primary)',
                  cursor: 'pointer'
                }}
                aria-label="Cancel folder selection"
              >
                Cancel
              </button>
              <button
                onClick={handleCustomFolderSelect}
                disabled={!customFolderPath.trim()}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: customFolderPath.trim() ? 'var(--obsidian-accent)' : 'var(--obsidian-border)',
                  color: 'white',
                  cursor: customFolderPath.trim() ? 'pointer' : 'not-allowed'
                }}
                aria-label={customFolderPath.trim() ? `Select folder: ${customFolderPath}` : 'Enter a folder path to select'}
              >
                Select Folder
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default LeftSidebar;
